import { Action, Selector, State, StateContext, Store, createPropertySelectors } from '@ngxs/store';
import { Injectable, inject } from '@angular/core';
import { patch } from '@ngxs/store/operators';
import { orderBy as _orderBy, keyBy as _keyBy } from 'lodash-es';

import { IEventorStateModel } from './eventor.models';
import {
    eventorStateDefaults,
    buildPlayerEventsToGamePlayerShiftEvents,
    buildShiftEventsToGamePlayerShiftEvents,
    buildGameEventsToGamePlayerShiftEvents,
    eventTypeOrder,
    findAdjacentEvent
} from './eventor.utils';
import { SetSelectedPlayerForTeamId, SetActiveRosterSort, ToggleEventingOverlayVisibility, SetSelectedTeamIdForEvents, SelectAdjacentEvent, ToggleShiftsForEvent, ToggleUserCreatedEvents, ToggleEventingOverlayPosition, ClearSelectedPlayerAndEvent, ClearSelectedSurfaceCoord, SetSelectedSurfaceClick } from './eventor.actions';

import { RosterState } from 'src/app/states/roster/roster.state';
import { TeamState } from 'src/app/states/team/team.state';
import { IRoster, IRosterPlayer } from 'src/app/states/roster/roster.models';
import { IRosterListItem, RosterListSortProperty } from 'src/app/components/ea-roster-list/ea-roster-list.models';
import { EaSortingDirection } from 'src/app/components/ea-button/ea-button.models';
import { EventState } from 'src/app/states/event/event.state';
import { IFlagDefinition, IGameEvent, IGameEventDefinition, IPlayerEvent, IPlayerEventDefinition, IPlayerEventShiftsApiResponse, IShiftEventDefinition } from 'src/app/states/event/event.models';
import { ITeam } from 'src/app/states/team/team.models';
import { GameState } from 'src/app/states/game/game.state';
import { SportState } from 'src/app/states/sport/sport.state';
import { IGame, IGamePeriod, IVidParam } from 'src/app/states/game/game.models';
import { QueryParamsChanged, UpdateQueryParams } from 'src/services/query-params/query-params.actions';
import { EEventItemType } from 'src/app/components/ea-event-item/ea-event.models';
import { IGamePlayerShiftEvent } from 'src/app/components/ea-event-item/ea-event.models';
import { convertFramesToSeconds, convertSecondsToStringTimestamp } from 'src/app/containers/ea-video-player/ea-video-player.utils';

@Injectable()
@State<IEventorStateModel>({
    name: eventorStateDefaults.sliceName,
    defaults: eventorStateDefaults.defaults
})
export class EventorState {
    private readonly _store = inject(Store);

    public static selectors = createPropertySelectors<IEventorStateModel>(EventorState);

    @Selector([
        RosterState.selectors.roster,
        TeamState.selectors.selectedTeamId
    ])
    public static selectedRosterForTeam(
        roster: IRoster | undefined,
        selectedTeamId: string | undefined
    ): IRosterListItem[] | undefined {
        if (!selectedTeamId || !roster) {
            return;
        }
        const players = roster.teams[selectedTeamId].players;

        // eslint-disable-next-line complexity
        return players.map((player) => {
            const person = roster.persons?.[player.id];

            return {
                id: player.id,
                jerseyNum: player.jerseyNum ? parseInt(player.jerseyNum, 10) : -1,
                position: player.position,
                primaryPosition: player.primaryPosition,
                firstName: person?.firstName,
                lastName: person?.lastName,
                laterality: person?.laterality
            };
        });
    }

    @Selector([
        EventorState.gameAndPlayerEvents,
        EventorState.selectors.queryParamEventId
    ])
    public static selectedGameAndPlayerEvent(
        events: IGamePlayerShiftEvent[],
        selectedEventId: string | undefined
    ): IGamePlayerShiftEvent | undefined {
        if (!events) {
            return;
        }

        return events.find(event => event.id === selectedEventId);
    }

    // eslint-disable-next-line complexity
    @Selector([
        GameState.selectors.game,
        GameState.selectors.selectedPeriod,
        GameState.selectors.periods,
    ])
    public static periodTimeDuration(
        game: IGame | undefined,
        selectedPeriod: string | undefined,
        periods: Record<string, IGamePeriod> | null
    ): number {
        if (!game) {
            return 0;
        }

        const period = selectedPeriod ?? '1';
        return periods?.[period]?.lengths.actual ?? 0;
    }

    // eslint-disable-next-line complexity
    @Selector([
        EventState.selectors.gameEvents,
        EventState.selectors.playerEvents,
        RosterState.selectors.roster,
        EventState.selectors.gameEventDefinitions,
        EventState.selectors.playerEventDefinitions,
        EventState.selectors.flagDefinitions,
        EventState.selectors.playerShiftEvents,
        EventState.selectors.shiftEventDefinitions,
        TeamState.selectors.teamsById,
        GameState.selectors.vidParams,
        EventorState.periodTimeDuration,
        SportState.gameClockGoesDown
    ])
    public static gameAndPlayerEvents(
        gameEvents: IGameEvent[] | undefined,
        playerEvents: IPlayerEvent[] | undefined,
        roster: IRoster | undefined,
        gameEventDefinitions: IGameEventDefinition[] | undefined,
        playerEventDefinitions: IPlayerEventDefinition[] | undefined,
        flagDefinitions: IFlagDefinition[] | undefined,
        playerShiftEvents: IPlayerEventShiftsApiResponse[] | undefined,
        shiftDefinitions: IShiftEventDefinition[] | undefined,
        teamsById: Record<string, ITeam> | undefined,
        vidParams: IVidParam[] | undefined,
        periodTimeDuration: number,
        gameClockGoesDown: boolean
    ): IGamePlayerShiftEvent[] {
        if (!gameEvents) {
            return [];
        }
        const gameEventDefinitionsById: Record<string, IGameEventDefinition> = _keyBy(gameEventDefinitions, (def) => def.id);
        const playerEventDefinitionsById: Record<string, IPlayerEventDefinition> = _keyBy(playerEventDefinitions, (def) => def.id);
        const shiftEventDefinitionsById: Record<string, IShiftEventDefinition> = _keyBy(shiftDefinitions, (def) => def.id);

        const personsById = roster?.persons;
        const bothTeamPlayersById: Record<string, IRosterPlayer> = {};

        if (roster) {
            for (const team of Object.values(roster.teams)) {
                for (const player of team.players) bothTeamPlayersById[player.id] = player;
            }
        }

        const flagNamesById: Record<string, string> = {};
        if (flagDefinitions) {
            for (const def of flagDefinitions) flagNamesById[def.id] = def.label;
        }

        const frameRate = vidParams?.[0]?.frameRate ?? 30;

        const eventorPlayerEvents = playerEvents?.filter(
            (playerEvent) => playerEventDefinitionsById[playerEvent.defId]?.isRelatedToGameEvents === false
        );

        const mergedEvents: IGamePlayerShiftEvent[] = [
            ...buildShiftEventsToGamePlayerShiftEvents(
                playerShiftEvents,
                shiftEventDefinitionsById,
                bothTeamPlayersById,
                personsById,
                teamsById,
                frameRate
            ),
            ...buildPlayerEventsToGamePlayerShiftEvents(
                eventorPlayerEvents,
                playerEventDefinitionsById,
                flagNamesById,
                bothTeamPlayersById,
                personsById,
                teamsById,
                frameRate
            ),
            ...buildGameEventsToGamePlayerShiftEvents(
                gameEvents,
                gameEventDefinitionsById,
                bothTeamPlayersById,
                teamsById,
                frameRate,
                periodTimeDuration,
                gameClockGoesDown
            ),
        ];

        return _orderBy(
            mergedEvents,
            ['period', 'frame', (event: IGamePlayerShiftEvent) => eventTypeOrder[event.eventType]],
            ['asc', 'asc', 'asc']
        );
    }

    @Selector([
        EventorState.selectors.selectedTeamIdForEvents,
        EventorState.selectors.selectedToggleShiftsForEvent,
        EventorState.gameAndPlayerEvents
    ])
    public static filteredGameAndPlayerEvents(
        selectedTeamIdForEvents: string | undefined,
        selectedShowShiftsForEvent: boolean,
        gameAndPlayerEvents: IGamePlayerShiftEvent[]
    ): IGamePlayerShiftEvent[] {
        // eslint-disable-next-line complexity
        return gameAndPlayerEvents.filter(event => {
            const matchesTeam = !selectedTeamIdForEvents || event.teamId === selectedTeamIdForEvents || event.teamId === null;
            const matchesShifts = selectedShowShiftsForEvent || event.eventType !== EEventItemType.SHIFT;

            return matchesTeam && matchesShifts;
        });
    }

    @Selector([
        EventorState.selectors.selectedPlayerIdByTeamId,
        TeamState.selectors.selectedTeamId
    ])
    public static selectedPlayerIdForTeam(
        selectedPlayerIdByTeamId: Record<string, string>,
        selectedTeamId: string | undefined
    ): string | undefined {
        if (!selectedTeamId ) {
            return;
        }

        return selectedPlayerIdByTeamId[selectedTeamId];
    }

    @Selector([
        EventorState.selectedRosterForTeam,
        EventorState.selectors.selectedSortRosterListProperty,
        EventorState.selectors.rosterListSortDirection
    ])
    public static sortedSelectedRosterForTeam(
        selectedRosterForTeam: IRosterListItem[] | undefined,
        rosterListProperty: RosterListSortProperty,
        rosterListSortDirection: EaSortingDirection
    ): IRosterListItem[] | undefined {
        if (!selectedRosterForTeam || !rosterListProperty) {
            return selectedRosterForTeam;
        }

        const sortByJerseyNum = ((e: IRosterListItem) => e.jerseyNum === -1 ? Infinity : e.jerseyNum);

        if (rosterListProperty === RosterListSortProperty.NUMBER) {
            return _orderBy(selectedRosterForTeam, sortByJerseyNum, [rosterListSortDirection]);
        }

        return _orderBy(
            selectedRosterForTeam,
            [rosterListProperty, sortByJerseyNum],
            [rosterListSortDirection, rosterListSortDirection]
        );
    }

    @Action(SetSelectedPlayerForTeamId)
    private _setSelectedPlayerForTeamId(ctx: StateContext<IEventorStateModel>, { playerId, teamId }: SetSelectedPlayerForTeamId) {
        return ctx.setState(
            patch<IEventorStateModel>({
                selectedPlayerIdByTeamId: patch({
                    [teamId]: playerId,
                }),
            }),
        );
    }

    @Action(ClearSelectedPlayerAndEvent)
    private _clearSelectedPlayerAndEvent(ctx: StateContext<IEventorStateModel>) {
        return ctx.patchState({
            selectedPlayerIdByTeamId: eventorStateDefaults.defaults.selectedPlayerIdByTeamId,
            queryParamEventId: eventorStateDefaults.defaults.queryParamEventId,
            clickedSurfaceCoord: eventorStateDefaults.defaults.clickedSurfaceCoord,
        });
    }

    @Action(SetSelectedSurfaceClick)
    private _setSelectedSurfaceClick(ctx: StateContext<IEventorStateModel>, { surfaceClick }: SetSelectedSurfaceClick) {
        return ctx.patchState({
            clickedSurfaceCoord: surfaceClick
        });
    }

    @Action(ClearSelectedSurfaceCoord)
    private _clearSelectedSurfaceCoord(ctx: StateContext<IEventorStateModel>) {
        return ctx.patchState({
            clickedSurfaceCoord: eventorStateDefaults.defaults.clickedSurfaceCoord
        });
    }

    @Action(SetActiveRosterSort)
    private _setActiveRosterSort(ctx: StateContext<IEventorStateModel>, { selectedSortRosterListProperty }: SetActiveRosterSort) {
        const state = ctx.getState();
        const isSameSortSelected = state.selectedSortRosterListProperty === selectedSortRosterListProperty;
        const currentDirection = state.rosterListSortDirection;

        const newDirection = isSameSortSelected && currentDirection === EaSortingDirection.ASC
            ? EaSortingDirection.DESC
            : EaSortingDirection.ASC;

        return ctx.patchState({
            selectedSortRosterListProperty,
            rosterListSortDirection: newDirection
        });
    }

    @Action(ToggleEventingOverlayVisibility)
    private _toggleEventingOverlayVisibility(ctx: StateContext<IEventorStateModel>) {
        return ctx.patchState({
            eventingOverlayHidden: !ctx.getState().eventingOverlayHidden
        });
    }

    @Action(ToggleEventingOverlayPosition)
    private _toggleEventingOverlayPosition(ctx: StateContext<IEventorStateModel>) {
        return ctx.patchState({
            eventingOverlayDocked: !ctx.getState().eventingOverlayDocked
        });
    }

    @Action(SelectAdjacentEvent)
    private _selectAdjacentEvent(ctx: StateContext<IEventorStateModel>, { direction }: SelectAdjacentEvent) {
        const events = this._store.selectSnapshot(EventorState.filteredGameAndPlayerEvents);
        const selectedEvent = this._store.selectSnapshot(EventorState.selectedGameAndPlayerEvent);

        const targetEvent = findAdjacentEvent(events, selectedEvent, direction);
        if (!targetEvent) {
            return;
        }

        const seconds = convertFramesToSeconds(targetEvent.frame, targetEvent.frameRate);
        const timestamp = convertSecondsToStringTimestamp(seconds, true, targetEvent.frameRate);

        return ctx.dispatch(new UpdateQueryParams({ eventid: targetEvent.id, timestamp }));
    }

    @Action(SetSelectedTeamIdForEvents)
    private _setSelectedTeamIdForEvents(ctx: StateContext<IEventorStateModel>, { teamId }: SetSelectedTeamIdForEvents) {
        return ctx.patchState({
            selectedTeamIdForEvents: teamId
        });
    }

    @Action(ToggleShiftsForEvent)
    private _toggleShiftsForEvent(ctx: StateContext<IEventorStateModel>, { value }: ToggleShiftsForEvent) {
        ctx.patchState({
            selectedToggleShiftsForEvent: value
        });
    }

    @Action(ToggleUserCreatedEvents)
    private _toggleUserCreatedEvents(ctx: StateContext<IEventorStateModel>, { value }: ToggleUserCreatedEvents) {
        ctx.patchState({
            selectedToggleUserCreatedEvents: value
        });
    }

    @Action(QueryParamsChanged)
    private _queryParamsChanged(ctx: StateContext<IEventorStateModel>, { queryParams }: QueryParamsChanged) {
        return ctx.patchState({
            queryParamEventId: queryParams.eventid ?? undefined
        });
    }
}
